import Image from 'next/image';

import Link from '@/components/link';
import Badge from '@/components/blog/badge';
import Authors from '@/components/blog/authors';
import { truncateText } from '@/utils/resolvers';
import { formatDate } from '@/utils/date';

import { Post } from '@/types';

export function ReadMoreLink({ href }: { href: string }) {
  return (
    <Link
      href={href}
      className="text-sm font-medium text-cornflower group flex items-center"
    >
      Read post
      <svg
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
        className="w-4 h-4 duration-100 group-hover:translate-x-1 inline-flex ml-2 transform transition-transform"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10.4714 3.52876C10.2111 3.26841 9.78898 3.26841 9.52865 3.52876C9.26831 3.78911 9.26831 4.21122 9.52865 4.47157L12.3906 7.3335H2.00004C1.63185 7.3335 1.33337 7.63196 1.33337 8.00016C1.33337 8.36836 1.63185 8.66683 2.00004 8.66683H12.3906L9.52865 11.5288C9.26831 11.7891 9.26831 12.2112 9.52865 12.4716C9.78898 12.7319 10.2111 12.7319 10.4714 12.4716L14.469 8.47402C14.4772 8.46593 14.4851 8.45763 14.4929 8.44913C14.5429 8.39432 14.5821 8.33317 14.6105 8.26843C14.6467 8.18635 14.6667 8.0956 14.6667 8.00016C14.6667 7.90473 14.6467 7.81398 14.6105 7.73189C14.5821 7.66715 14.5429 7.60601 14.4929 7.55119C14.4851 7.54269 14.4772 7.53439 14.469 7.52631L10.4714 3.52876Z"
          fill="currentColor"
        />
      </svg>
    </Link>
  );
}

export default function Card({ post }: { post: Post }) {
  return (
    <article className="w-full flex flex-col" key={post.id}>
      <div className="flex-shrink-0 blog-image">
        {post.coverImage?.url && (
          <Link href={`/blog/${post.slug}`} className="blog-card-image">
            <Image
              src={post.coverImage.url}
              alt={post.coverImage.alt}
              width={384}
              height={216}
              quality={100}
              layout="responsive"
              objectFit="cover"
              className="rounded bg-catskill-white"
            />
          </Link>
        )}
      </div>

      <div className="flex-1 md:px-2 py-4 flex flex-col blog-card-text">
        <div className="flex-1">
          <div className="space-x-1">
            {post.categories.length > 0 &&
              post.categories.map((category, index) => {
                // If there are more than two categories, we show just the first ones
                if (index > 1) return null;

                return (
                  <Badge
                    key={category.slug}
                    slug={`/blog/tag/${category.slug}`}
                    title={category.title}
                    variant="primary"
                  />
                );
              })}
          </div>

          <Link
            href={`/blog/${post.slug}`}
            className="hover:text-cornflower transition-colors"
          >
            <h3 className="mt-4 mb-5 font-bold text-[22px] leading-tight blog-card-title">
              {post.title}
            </h3>

            <p className="text-light-haiti leading-normal">
              {truncateText({ text: post.subTitle })}
            </p>
          </Link>
        </div>

        <div className="mt-6 flex justify-between">
          <div className="flex">
            {post.authors.length > 0 && (
              <Authors authors={post.authors} type="small" />
            )}

            <div className="h-full text-xs text-zodiac uppercase ml-2">
              <time
                dateTime={post.publishedOn}
                className="h-full flex items-center pt-[1px]"
              >
                {formatDate(post.publishedOn)}
              </time>
            </div>
          </div>

          <ReadMoreLink href={`/blog/${post.slug}`} />
        </div>
      </div>
    </article>
  );
}
